import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";

// Define a type for data payloads
type RequestData = Record<string, any>;

// Create the Axios instance
const axiosInstance: AxiosInstance = axios.create({
  baseURL: "/api",
  timeout: 30000,
});

// Function to retrieve all headers, including dynamic ones
const getHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  const authToken = localStorage.getItem("authorizationToken");
  const tenant = localStorage.getItem("currentTenant");
  const email = localStorage.getItem("currentUserEmail");

  if (authToken) {
    headers["Authorization"] = `Bearer ${authToken}`;
  }

  if (tenant) {
    headers["tenant"] = tenant;
  }

  if (email) {
    headers["email"] = email;
  }

  return headers;
};

// Generic request function
const request = async <T = any>(
  method: "get" | "post" | "put" | "delete" | "patch",
  url: string,
  data: RequestData = {},
  config: AxiosRequestConfig = {}
): Promise<T> => {
  try {
    // Merge default headers with any custom headers from config
    const headers = { ...getHeaders(), ...config.headers };

    const response: AxiosResponse<T> = await axiosInstance.request<T>({
      method,
      url,
      data,
      ...config,
      headers,
    });
    return response.data;
  } catch (error) {
    console.error(`Error in ${method.toUpperCase()} request to ${url}:`, error);
    throw error;
  }
};

// Individual functions for common HTTP methods
const get = async <T = any>(url: string, config: AxiosRequestConfig = {}): Promise<T> => {
  return request<T>("get", url, {}, config);
};

const post = async <T = any>(
  url: string,
  data: RequestData = {},
  config: AxiosRequestConfig = {}
): Promise<T> => {
  return request<T>("post", url, data, config);
};

const put = async <T = any>(
  url: string,
  data: RequestData = {},
  config: AxiosRequestConfig = {}
): Promise<T> => {
  return request<T>("put", url, data, config);
};

const del = async <T = any>(
  url: string,
  config: AxiosRequestConfig = {},
  data: RequestData = {}
): Promise<T> => {
  return request<T>("delete", url, data, config);
};

const patch = async <T = any>(
  url: string,
  data: RequestData = {},
  config: AxiosRequestConfig = {}
): Promise<T> => {
  return request<T>("patch", url, data, config);
};
export { axiosInstance as requests, request, get, post, put, del, patch };
